/**
 * Todo 入力ボックス (インライン)
 *
 * Enter で改行、⌘/Ctrl+Enter で追加。入力に応じて高さが伸びる。
 * 普段は 1 行だけ表示し、フォーカス中・入力中のみプロジェクト選択を展開する。
 * 選んだプロジェクトは折りたたんでも保持し、次に展開したときに引き継ぐ。
 * プロジェクト選択は既定で入力欄の下に重ねて出す。開閉で下の一覧がずれると、閉じた拍子に別の行を押してしまうため。
 * ドラフト (text) は Zustand に保持し、タブ移動でも消えない。
 */

import { CornerDownLeft, Plus } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TodoResponse } from '@/api/types/todo'
import { useFocusWithin } from '@/hooks/useFocusWithin'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { cn } from '@/lib/utils'
import { useTodoStore } from '@/store/useTodoStore'

import { ProjectPicker } from './ProjectPicker'

type Props = {
  /** 追加に成功したとき（Home のクイック追加ダイアログを閉じるのに使う） */
  onAdded?: (todo: TodoResponse) => void
  /** プロジェクト選択の出し方。overlay は下の要素に重ねる、inline は下に差し込む（下に一覧が無い場所向け） */
  pickerPlacement?: 'overlay' | 'inline'
}

export const TodoInput = ({ onAdded, pickerPlacement = 'overlay' }: Props = {}) => {
  const text = useTodoStore((s) => s.draftText)
  const setText = useTodoStore((s) => s.setDraftText)
  const resetDraft = useTodoStore((s) => s.resetDraft)
  const addTodo = useTodoStore((s) => s.addTodo)
  const todos = useTodoStore((s) => s.todos)
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const { isSubmitting, run } = useSubmitLock()
  const containerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // ボックス内 (プロジェクト名入力など) へのフォーカス移動では閉じない
  const isFocused = useFocusWithin(containerRef)

  const hasText = text.trim().length > 0
  const isExpanded = isFocused || hasText

  // 追加ボタン・プロジェクト名入力欄が消えるとフォーカスが外れるため、続けて入力できるよう入力欄へ戻す
  const keepFocusInBox = () => {
    if (!containerRef.current?.contains(document.activeElement)) return
    textareaRef.current?.focus()
  }

  const handleAdd = async () => {
    if (!hasText) return
    await run(async () => {
      try {
        const created = await addTodo({ title: text.trim(), source: 'manual', project: selectedProject })
        toast.success('Todoを追加しました')
        keepFocusInBox()
        resetDraft()
        onAdded?.(created)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '保存に失敗しました')
      }
    })
  }

  return (
    <div ref={containerRef} className="relative flex flex-col gap-2">
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg border px-3 py-2 min-h-10 transition-colors',
          isFocused ? 'border-primary/50 bg-muted/30' : 'border-transparent bg-muted/20 hover:bg-muted/30',
        )}
      >
        <Plus className={cn('size-4 shrink-0 mt-0.5', isFocused ? 'text-primary' : 'text-muted-foreground')} />
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void handleAdd()
            }
            if (e.key === 'Escape') e.currentTarget.blur()
          }}
          placeholder="タスクを追加…（⌘Enter で追加）"
          aria-label="タスクを追加"
          className="flex-1 min-w-0 max-h-40 resize-none field-sizing-content bg-transparent text-sm leading-5 outline-none placeholder:text-muted-foreground"
        />
        {hasText ? (
          <button
            type="button"
            // クリックで入力欄のフォーカスを奪わない
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void handleAdd()}
            disabled={isSubmitting}
            aria-label="追加"
            className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 animate-in fade-in"
          >
            <CornerDownLeft className="size-3.5" />
            {isSubmitting ? '追加中' : '追加'}
          </button>
        ) : null}
      </div>

      {isExpanded ? (
        <div
          className={cn(
            'animate-in fade-in slide-in-from-top-1 duration-150',
            // カレンダービューでは直下に sticky ツールバー (z-40) が来るため、それより手前に出す
            pickerPlacement === 'overlay'
              ? 'absolute inset-x-0 top-full z-50 mt-1 rounded-lg border bg-popover px-2 py-1.5 shadow-md'
              : 'px-1',
          )}
          // チップのクリックで入力欄のフォーカスが外れて折りたたまれるのを防ぐ (プロジェクト名入力欄は除く)
          onMouseDown={(e) => {
            if (!(e.target instanceof HTMLInputElement)) e.preventDefault()
          }}
        >
          <ProjectPicker
            todos={todos}
            value={selectedProject}
            onChange={setSelectedProject}
            onInputClose={keepFocusInBox}
          />
        </div>
      ) : null}
    </div>
  )
}
